import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import useExperiencePlayerLaunch from 'hooks/useExperiencePlayerLaunch'
import { getExpireAtFromExpiration } from './helpers'
import ExperiencePlayerLaunchButton from './component'

const openLaunchLink = url => {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

const ExperiencePlayerLaunchButtonContainer = ({
  dataTestId,
  expiration,
  forceFixedWidth,
  isHighSchool,
  label,
  lessonId,
}) => {
  // The hook hydrates any saved link from local storage on mount and validates
  // it against the experience-player service, so `link`/`isExpired` already
  // reflect whether an unexpired link exists for this lesson.
  const {
    link,
    isCreating,
    isExpired,
    generateLink,
  } = useExperiencePlayerLaunch({ lessonId })

  const hasActiveLinkForLesson =
    !!link?.url && !isExpired && link.lessonId === lessonId

  const handleClick = useCallback(async () => {
    if (hasActiveLinkForLesson) {
      openLaunchLink(link.url)
      return
    }

    // Open a blank tab synchronously to avoid popup blockers, then navigate once
    // the generated URL is available.
    const newTab = window.open('', '_blank')
    if (newTab) newTab.opener = null

    // Use the absolute expiration from content as the link's expireAt rather
    // than a relative TTL.
    const expireAt = getExpireAtFromExpiration(expiration)
    const result = await generateLink({ lessonId, expireAt })

    if (result?.url) {
      if (newTab) {
        newTab.location.href = result.url
      } else {
        openLaunchLink(result.url)
      }
    } else if (newTab) {
      newTab.close()
    }
  }, [hasActiveLinkForLesson, link, expiration, lessonId, generateLink])

  if (!lessonId) {
    return null
  }

  return (
    <ExperiencePlayerLaunchButton
      dataTestId={dataTestId}
      expiresAt={hasActiveLinkForLesson ? link.expiresAt : null}
      forceFixedWidth={forceFixedWidth}
      isCreating={isCreating}
      isHighSchool={isHighSchool}
      label={label}
      onClick={handleClick}
    />
  )
}

ExperiencePlayerLaunchButtonContainer.propTypes = {
  dataTestId: PropTypes.string,
  // Absolute ISO 8601 expiration from the riseActivity content (e.g.
  // `20270624T000000-0700`), converted to the service `expireAt` parameter.
  expiration: PropTypes.string,
  forceFixedWidth: PropTypes.bool,
  isHighSchool: PropTypes.bool,
  label: PropTypes.string,
  lessonId: PropTypes.string,
}

export default ExperiencePlayerLaunchButtonContainer
